import {
  AnyObject,
  FileFormat,
  MonthOptions,
  OptionProps,
  WindowDevice
} from 'typings/custom'
import { Demographic } from 'typings/demographic'
import { isAfter, isBefore } from 'date-fns'
import config from 'const/config'
import { AccountSummaryConditions } from 'services/accountSummary/typings'
import routePaths from 'const/routePaths'

const { ASSET_HOST } = config

const ibpMenuItem = ['invoiceBillPay', 'ibpStatus']

export const assetsUrl = `${ASSET_HOST}`

export const isIE = (): boolean =>
  window.navigator.userAgent.indexOf('MSE') !== -1 ||
  window.navigator.userAgent.indexOf('Trident/') !== -1

export const isEdge = (): boolean =>
  window.navigator.userAgent.indexOf('Edge') !== -1 ||
  window.navigator.userAgent.indexOf('Edg') !== -1

export const isSafari = (): boolean =>
  window.navigator.vendor.indexOf('Apple') > -1 &&
  window.navigator.userAgent.indexOf('CriOS') === -1 &&
  window.navigator.userAgent.indexOf('FxiOS') === -1

export const getConsolidateAmount = (selectedItems: AnyObject = []): number => {
  let totalAmount = 0
  selectedItems.map(({ updatedAmount, balanceAmtDue }: AnyObject) => {
    // eslint-disable-next-line no-restricted-globals
    totalAmount += isNaN(updatedAmount || balanceAmtDue)
      ? 0
      : parseFloat(updatedAmount || balanceAmtDue)
    return updatedAmount
  })
  return totalAmount
}

// this method is for calculating totalAmount for credits
export const creditConsolidateAmount = (
  selectedItems: AnyObject = []
): number => {
  let totalAmount = 0
  selectedItems.map(({ updatedAmount, balanceAmtDue }: AnyObject) => {
    // eslint-disable-next-line no-restricted-globals
    totalAmount += isNaN(updatedAmount || balanceAmtDue)
      ? 0
      : parseFloat(Math.abs(updatedAmount || balanceAmtDue).toString())
    return updatedAmount
  })
  return totalAmount
}

export const monthOptions: Array<MonthOptions> = [
  {
    label: 'January',
    value: '00',
    days: 31
  },
  {
    label: 'February',
    value: '01',
    days: 29
  },
  {
    label: 'March',
    value: '02',
    days: 31
  },
  {
    label: 'April',
    value: '03',
    days: 30
  },
  {
    label: 'May',
    value: '04',
    days: 31
  },
  {
    label: 'June',
    value: '05',
    days: 30
  },
  {
    label: 'July',
    value: '06',
    days: 31
  },
  {
    label: 'August',
    value: '07',
    days: 31
  },
  {
    label: 'September',
    value: '08',
    days: 30
  },
  {
    label: 'October',
    value: '09',
    days: 31
  },
  {
    label: 'November',
    value: '10',
    days: 30
  },
  {
    label: 'December',
    value: '11',
    days: 31
  }
]

export const populateDayDropDown = (month: string): Array<OptionProps> => {
  const Options = []
  const noOfDays = (
    monthOptions.filter(({ value }: MonthOptions) => value === month)[0] || {}
  ).days
  for (
    let dayValue = 1;
    dayValue <= noOfDays;
    // eslint-disable-next-line no-plusplus
    dayValue++
  )
    Options.push({
      label: `${dayValue}`,
      value: `${dayValue}`
    })
  return Options
}

export const generateTrackingID = (transidCount = 13): string => {
  return (Math.random() * 1e32).toString(36).slice(3, transidCount)
}

export const getYearOptions = (start = 0, limit = 0): Array<OptionProps> => {
  const startYear = new Date().getFullYear() - start
  const endYear = startYear + limit
  const yearOptions: Array<OptionProps> = []
  // eslint-disable-next-line no-plusplus
  for (let year = startYear; year < endYear; year++) {
    yearOptions.push({
      label: year.toString(),
      value: year.toString()
    })
  }
  return yearOptions
}

export const getMonthName = (monthValue: string): string => {
  const { label: monthName = '' } =
    monthOptions.find(({ value }) => value === monthValue) || {}
  return monthName
}

export const invoiceFilterOptions = [
  {
    value: 'transactionId',
    label: 'Invoice No.',
    filterType: 'stringDigitWithHypen'
  },
  {
    value: 'purchaseOrder',
    label: 'PO No./ Job Name',
    filterType: 'stringDigitWithHypen'
  }
]

export interface WindowWithTelemetry extends Window {
  bmak: {
    // eslint-disable-next-line camelcase
    get_telemetry: () => string
  }
}

export const addBmTelemetryToHeaders = (reqHeaders: AnyObject): AnyObject => {
  const { bmak } = (window as unknown as WindowWithTelemetry) || {}
  const updatedHeaders = { ...reqHeaders }
  if (bmak && bmak.get_telemetry) {
    updatedHeaders['BM-Telemetry'] = bmak.get_telemetry()
  }
  return {
    ...updatedHeaders
  }
}

export const getSortedAccounts = (
  availableAccounts: Array<Demographic>
): Array<Demographic> =>
  [...availableAccounts]
    .sort((item1: Demographic, item2: Demographic) =>
      item1.lastFourDigitAccountNumber > item2.lastFourDigitAccountNumber
        ? 1
        : -1
    )
    .sort((item: Demographic) => (item.relationship === 'PARENT' ? -1 : 1))

export const getOptionsFromObject = (
  payload: AnyObject
): Array<OptionProps> => {
  return Object.keys(payload).map(key => {
    return { label: payload[key], value: key }
  })
}

export const isLoginFlowPage = (pathName: string): boolean => {
  const loginUrlList = [
    routePaths.home,
    routePaths.resetPassword,
    routePaths.error,
    routePaths.registration,
    routePaths.firstTimeLogin,
    routePaths.firstTimeLoginConfirmation,
    routePaths.registerChildAccount,
    routePaths.updatePassword,
    routePaths.sessionExpired,
    routePaths.recoverUserId
  ]
  return loginUrlList.includes(pathName)
}

export const getIBPMenu = (ibpFlag: string): Array<string> => {
  let ibpMenu: Array<string> = []
  if (ibpFlag === 'N' || !ibpFlag) {
    ibpMenu = [...ibpMenuItem]
  } else if (ibpFlag !== 'E') {
    ibpMenu = [ibpMenuItem[0]]
  } else if (ibpFlag === 'E') {
    ibpMenu = [ibpMenuItem[1]]
  }
  return ibpMenu
}

/**
 * Prevent timezone from manipulating date. Use for converting strings to date
 * without timezone issues.
 */
export const getDateWithTimezoneOffset = (dt: Date): Date =>
  new Date(dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000)

export const checkIsUnderMaintenance = (
  startTime: string,
  endTime: string
): boolean =>
  isAfter(new Date(), new Date(startTime)) &&
  isBefore(new Date(), new Date(endTime))

export const isAccountClosed = (
  item: AccountSummaryConditions | AccountSummaryConditions[]
): boolean => {
  return item.includes(
    AccountSummaryConditions.Closed ||
      AccountSummaryConditions.ClosedWithBalance
  )
}

export const removeAmountFormat = (formattedAmount = ''): string => {
  const unFormattedAmount = formattedAmount.replace(/[$,]/g, '')
  if (unFormattedAmount === '.') {
    return ''
  }
  return unFormattedAmount
}

export const mimeType: Record<FileFormat, string> = {
  csv: 'text/csv',
  excel: 'application/ms-excel',
  quickbook: 'application/vnd.intu.QBO',
  quicken: 'application/vnd.intu.QFX'
}

export const noOperations = (): void => {
  // Used for define default function
}

/** Returns true if the given date is valid */
export const isValidDateValue = (date: Date) =>
  date ? !Number.isNaN(date?.valueOf()) : false

/*
 * Returns the device print value
 * If application is running in local host, unable to get window object from external js file devicePrint.js.
 * But if it is PCF environment,able to take devicePrint id. So, hardcoded value is given for local host app running
 */
export const getDeviceId = (): string => {
  const windowDevice = window as WindowDevice
  const deviceID =
    'version%3D3%2E5%2E1%5F4%26pm%5Ffpua%3Dmozilla%2F5%2E0%20%28windows%20nt%2010%2E0%3B%20wow64%29%20applewebkit%2F537%2E36%20%28khtml%2C%20like%20gecko%29%20chrome%2F85%2E0%2E4183%2E102%20safari%2F537%2E36%7C5%2E0%20%28Windows%20NT%2010%2E0%3B%20WOW64%29%20AppleWebKit%2F537%2E36%20%28KHTML%2C%20like%20Gecko%29%20Chrome%2F85%2E0%2E4183%2E102%20Safari%2F537%2E36%7CWin32%26pm%5Ffpsc%3D24%7C1920%7C1080%7C1040%26pm%5Ffpsw%3D%26pm%5Ffptz%3D5%2E5%26pm%5Ffpln%3Dlang%3Den%2DUS%7Csyslang%3D%7Cuserlang%3D%26pm%5Ffpjv%3D0%26pm%5Ffpco%3D1%26pm%5Ffpasw%3Dinternal%2Dpdf%2Dviewer%7Cmhjfbmdgcfjbbpaeojofohoefgiehjai%7Cinternal%2Dnacl%2Dplugin%26pm%5Ffpan%3DNetscape%26pm%5Ffpacn%3DMozilla%26pm%5Ffpol%3Dtrue%26pm%5Ffposp%3D%26pm%5Ffpup%3D%26pm%5Ffpsaw%3D1920%26pm%5Ffpspd%3D24%26pm%5Ffpsbd%3D%26pm%5Ffpsdx%3D%26pm%5Ffpsdy%3D%26pm%5Ffpslx%3D%26pm%5Ffpsly%3D%26pm%5Ffpsfse%3D%26pm%5Ffpsui%3D%26pm%5Fos%3DWindows%26pm%5Fbrmjv%3D85%26pm%5Fbr%3DChrome%26pm%5Finpt%3D%26pm%5Fexpt%3D'
  return windowDevice.encodeDevicePrint
    ? windowDevice.encodeDevicePrint()
    : deviceID
}
